import React, { useMemo } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { SERIF, SANS, MONO, todayTokens } from '../../theme/todayTokens';
import { recordStyles } from '../property/PropertyRecord';

/**
 * Shared "Today" primitives extracted from the migrated pages (Enquiries, Properties,
 * Maintenance, Compliance, Sales, Tenants). The Components page renders these as the
 * living style guide — prefer consuming them over redefining the styles per page.
 */

export const statusPillTones = (t, isDarkMode) => ({
  navy:  { bg: isDarkMode ? 'rgba(143,163,224,0.16)' : 'rgba(28,43,90,0.08)', fg: t.navy, dot: { borderRadius: '50%', bgcolor: t.navy } },
  amber: { bg: isDarkMode ? 'rgba(214,158,46,0.16)' : '#F6E7C8', fg: isDarkMode ? t.amberText : '#6E4700', dot: { borderRadius: '50%', border: `2px solid ${t.amber}` } },
  green: { bg: isDarkMode ? 'rgba(82,183,136,0.16)' : '#DCEBE2', fg: isDarkMode ? t.green : '#1F4D38', dot: { borderRadius: '50%', bgcolor: t.green } },
  rust:  { bg: isDarkMode ? 'rgba(240,113,74,0.16)' : '#F5DDD3', fg: isDarkMode ? t.rust : '#8A2A0B', dot: { borderRadius: '2px', bgcolor: t.rust } },
  muted: { bg: t.lineSoft, fg: t.body, dot: { borderRadius: '50%', bgcolor: t.muted } },
});

/**
 * Tone pill with dot — the standard status/badge chip.
 * `hollow` overrides the dot to an outlined circle; `square` to a filled square (urgent).
 * Pass `showDot={false}` for label-only chips.
 */
export function StatusPill({ tone = 'muted', label, hollow = false, square = false, showDot = true, sx, ...boxProps }) {
  const { isDarkMode } = useTheme();
  const t = todayTokens(isDarkMode);
  const tones = statusPillTones(t, isDarkMode);
  const style = tones[tone] || tones.muted;
  return (
    <Box
      component="span"
      sx={{
        minHeight: 26,
        display: 'inline-flex',
        alignItems: 'center',
        gap: showDot ? 1 : 0,
        px: 1.25,
        borderRadius: '13px',
        bgcolor: style.bg,
        color: style.fg,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
        ...sx,
      }}
      {...boxProps}
    >
      {showDot && (
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            boxSizing: 'border-box',
            flexShrink: 0,
            borderRadius: square ? '2px' : style.dot.borderRadius,
            bgcolor: hollow || style.dot.border ? 'transparent' : style.dot.bgcolor,
            border: hollow ? `2px solid ${style.fg}` : style.dot.border || 0,
          }}
        />
      )}
      {label}
    </Box>
  );
}

/** Mono uppercase field label. */
export function FieldLabel({ children, required, htmlFor, sx }) {
  const { isDarkMode } = useTheme();
  const t = todayTokens(isDarkMode);
  return (
    <Box
      component="label"
      htmlFor={htmlFor}
      sx={{
        display: 'block',
        mb: 0.75,
        fontFamily: MONO,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: t.muted,
        ...sx,
      }}
    >
      {children}
      {required && <Box component="span" sx={{ color: t.rust, ml: 0.5 }}>*</Box>}
    </Box>
  );
}

/** Rounded filter chip with dot — active state fills the dot and tints navy. */
export function FilterPill({ active, onClick, dotColor, children }) {
  const { isDarkMode } = useTheme();
  const s = recordStyles(isDarkMode);
  const { t } = s;
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={{
        minHeight: 36,
        px: 1.5,
        gap: 1,
        borderRadius: '18px',
        border: `1px solid ${active ? t.navy : t.lineStrong}`,
        bgcolor: active ? (isDarkMode ? 'rgba(143,163,224,0.12)' : 'rgba(28,43,90,0.06)') : 'transparent',
        fontFamily: SANS,
        fontSize: 13,
        fontWeight: 600,
        color: active ? t.ink : t.muted,
        ...s.focusRing,
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          boxSizing: 'border-box',
          bgcolor: active ? (dotColor || t.navy) : 'transparent',
          border: `2px solid ${active ? (dotColor || t.navy) : t.lineStrong}`,
        }}
      />
      {children}
    </ButtonBase>
  );
}

/** Mono uppercase sortable table header with rust direction arrows. */
export function SortHeader({ label, active, direction = 'asc', onClick }) {
  const { isDarkMode } = useTheme();
  const s = recordStyles(isDarkMode);
  const { t } = s;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        border: 0,
        bgcolor: 'transparent',
        cursor: 'pointer',
        p: 0,
        textAlign: 'left',
        fontFamily: SANS,
        fontSize: 11,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: active ? t.ink : t.muted,
        transition: 'color 0.15s ease',
        '&:hover': { color: t.ink },
        ...s.focusRing,
      }}
    >
      {label}
      {active && (direction === 'asc'
        ? <FaSortAlphaDown size={11} style={{ color: t.rust }} />
        : <FaSortAlphaUp size={11} style={{ color: t.rust }} />)}
    </Box>
  );
}

/** Summary cell — value + label in matching serif. Renders a button when `onClick` is set. */
export function StatCell({ label, value, onClick, ariaLabel }) {
  const { isDarkMode } = useTheme();
  const s = recordStyles(isDarkMode);
  const { t } = s;
  const cellSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: 1.25,
    p: { xs: 2.25, md: 2.75 },
    bgcolor: t.surface,
    textAlign: 'left',
    width: '100%',
    height: '100%',
    ...s.focusRing,
  };
  const valueSx = { fontFamily: SERIF, fontSize: { xs: 28, md: 34 }, lineHeight: 1, color: t.ink };
  const inner = (
    <>
      <Box component="span" sx={valueSx}>{value}</Box>
      <Box component="span" sx={valueSx}>{label}</Box>
    </>
  );
  if (!onClick) {
    return <Box sx={cellSx}>{inner}</Box>;
  }
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={ariaLabel || `${label}: ${value}`}
      sx={{ ...cellSx, '&:hover': { bgcolor: t.surfaceAlt } }}
    >
      {inner}
    </ButtonBase>
  );
}

/** Underline-only input styles. `hasError` swaps the underline to rust. */
export const lineControlSx = (t, isDarkMode, hasError) => ({
  width: '100%',
  minHeight: 44,
  boxSizing: 'border-box',
  px: 0,
  py: 1.25,
  border: 0,
  borderRadius: 0,
  borderBottom: `1px solid ${hasError ? t.rust : t.lineStrong}`,
  bgcolor: 'transparent',
  color: t.ink,
  fontFamily: SANS,
  fontSize: 15,
  fontWeight: 500,
  outline: 'none',
  appearance: 'none',
  colorScheme: isDarkMode ? 'dark' : 'light',
  transition: 'border-color 120ms ease',
  '&::placeholder': { color: t.muted, opacity: 1 },
  '&:hover': { borderBottomColor: hasError ? t.rust : t.navy },
  '&:focus': { borderBottomColor: hasError ? t.rust : t.navy },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

/**
 * Legacy native <select> underline styles.
 * Prefer `LineAutocomplete` from `ui/LineAutocomplete` for new dropdowns —
 * that component is the single source of truth for Today dropdown chrome.
 */
export const lineSelectSx = (t, isDarkMode, hasError) => ({
  ...lineControlSx(t, isDarkMode, hasError),
  cursor: 'pointer',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%235B5F68' d='M1 1l5 5 5-5'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0 center',
  paddingRight: 20,
});

/** 48px page-level hero buttons: outline (`heroBtn`) and navy (`navyHeroBtn`). */
export function useHeroButtons() {
  const { isDarkMode } = useTheme();
  const s = recordStyles(isDarkMode);
  const { t } = s;
  return useMemo(() => {
    const heroBtn = { ...s.outlineBtn, height: 48, px: 2.25, fontSize: 15, borderRadius: '12px', bgcolor: t.surfaceAlt };
    const navyHeroBtn = {
      ...heroBtn,
      border: 0,
      bgcolor: t.navy,
      color: isDarkMode ? t.panel : '#FFFFFF',
      '&:hover': { bgcolor: t.navy, opacity: 0.92 },
    };
    return { heroBtn, navyHeroBtn };
  }, [s, t, isDarkMode]);
}
